import React from 'react';
import { Dropdown, Form } from 'react-bootstrap';

export interface SlingLocationFiltersState {
  name?: string;
  address?: string;
  // TODO: Add more filters: location_id, state, mapped_to
}

interface SlingLocationFilterBarProps {
  filters: SlingLocationFiltersState;
  onFiltersChange: (filters: SlingLocationFiltersState) => void;
  onClearFilters: () => void;
}

interface TextFilterProps {
  field: keyof SlingLocationFiltersState;
  label: string;
  icon: string;
  placeholder: string;
  value?: string;
  onChange: (field: keyof SlingLocationFiltersState, value: string | undefined) => void;
  isClearing?: boolean;
}

const TextFilter: React.FC<TextFilterProps> = ({ field, label, icon, placeholder, value, onChange, isClearing }) => {
  const [tempValue, setTempValue] = React.useState(value || '');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Update temp value when value prop changes
  React.useEffect(() => {
    setTempValue(value || '');
    // Clear any pending debounced update when value is cleared externally
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [value]);

  // Clean up debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleApply = () => {
    // Don't apply if we're in the middle of clearing all filters
    if (isClearing) {
      return;
    }
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Only call onChange if value actually changed
    const newValue = tempValue || undefined;
    if (newValue !== value) {
      onChange(field, newValue);
    }
  };

  const handleInputChange = (newValue: string) => {
    setTempValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced update
    debounceTimerRef.current = setTimeout(() => {
      onChange(field, newValue || undefined);
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
      // Close the dropdown by clicking the toggle button
      const toggleButton = document.getElementById(`dropdown-${field}`);
      if (toggleButton) {
        toggleButton.click();
      }
    }
  };

  return (
    <Dropdown onToggle={(isOpen) => {
      if (!isOpen) {
        // Apply changes when dropdown closes
        handleApply();
      } else {
        // Focus input when dropdown opens
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }}>
      <Dropdown.Toggle
        variant={value ? "primary" : "outline-secondary"}
        size="sm"
        id={`dropdown-${field}`}
        className={value ? "d-flex align-items-center" : ""}
      >
        {value && (
          <>
            <i
              className="bi bi-x-circle me-2"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(field, undefined);
              }}
            ></i>
            <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
          </>
        )}
        <i className={`bi ${icon} me-1`}></i>
        {label}{value && `: ${value}`}
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-header-control" data-bs-theme="light">
        <div className="px-3 py-2" style={{ minWidth: '250px' }} onClick={(e) => e.stopPropagation()}>
          <Form.Control
            ref={inputRef}
            size="sm"
            type="text"
            placeholder={placeholder}
            value={tempValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const SlingLocationFilterBar: React.FC<SlingLocationFilterBarProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const [isClearing, setIsClearing] = React.useState(false);

  const handleFilterChange = (field: keyof SlingLocationFiltersState, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => value).length;
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="ms-1 bg-header-control border-bottom p-1 me-1 rounded">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <TextFilter
          field="name"
          label="Name"
          icon="bi-search"
          placeholder="Filter by location name..."
          value={filters.name}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <TextFilter
          field="address"
          label="Address"
          icon="bi-geo-alt"
          placeholder="Filter by address..."
          value={filters.address}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        {/* TODO: Add more filters */}
        {/* <TextFilter field="location_id" ... /> */}
        {/* <TextFilter field="state" ... /> */}
        {/* <SelectFilter field="mapped_to" ... /> */}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            className="btn btn-outline-danger btn-sm ms-auto"
            onClick={() => {
              setIsClearing(true);
              onClearFilters();
              setTimeout(() => setIsClearing(false), 0);
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default SlingLocationFilterBar;
